import concat from 'lodash/concat';
import uniqBy from 'lodash/uniqBy';

import { db, functions } from '.';

const getDetailsRef = challengeRef =>
  challengeRef.collection('data').doc('details');

const getEmptyDetails = () => ({
  relatedConcepts: [],
  relatedTopics: [],
  puzzle: '',
  questions: [],
  hintQuestionId: null,
  hasSubjective: false,
  criteria: [],
  solution: null,
  rereviewDescriptor: ''
});

async function addChallengeToDB({ name, poster }) {
  const challengeData = {
    name,
    poster,
    admin: false
  };

  const newChallengeRef = db.collection('challenges').doc();
  const newDetailsRef = getDetailsRef(newChallengeRef);

  await db.runTransaction(async transaction => {
    await transaction.set(newChallengeRef, challengeData);
    await transaction.set(newDetailsRef, getEmptyDetails());
  });

  return newChallengeRef;
}

async function getChallengeFromDB(id) {
  const challengeRef = db.collection('challenges').doc(id);
  const detailsRef = getDetailsRef(challengeRef);

  const challengeSnap = await challengeRef.get();
  if (challengeSnap.exists) {
    const challengeData = challengeSnap.data();
    const detailsSnap = await detailsRef.get();

    const topicFetchPromises = detailsSnap
      .get('relatedTopics')
      .map(topicRef => topicRef.get());

    const topicSnaps = await Promise.all(topicFetchPromises);

    const conceptFetchPromises = detailsSnap
      .get('relatedConcepts')
      .map(conceptRef => conceptRef.get());

    const conceptSnaps = await Promise.all(conceptFetchPromises);

    return {
      ...challengeData,
      id,
      details: {
        ...detailsSnap.data(),
        relatedTopics: topicSnaps.map(snap => {
          return { id: snap.id, name: snap.get('name') };
        }),
        relatedConcepts: conceptSnaps.map(snap => {
          const data = snap.data();
          return { id: snap.id, name: data.name, topicId: data.topicId.id };
        })
      }
    };
  } else {
    throw new Error('No challenge with given id!');
  }
}

/**
 * @param {String} challengeId - Challenge Id
 * @param {String} publishLevel - `admin` or `users`.
 */
async function publishChallenge(challengeId, publishLevel) {
  const publishChallengeCall = functions.httpsCallable(
    'adminChallengesPublish'
  );
  await publishChallengeCall({ challengeId, publishLevel });
}

async function updateChallengeInDB(data) {
  const {
    id,
    details: { relatedTopics, relatedConcepts, ...detailsData },
    ...docData
  } = data;

  detailsData.relatedTopics = uniqBy(
    concat(
      relatedTopics.map(o => db.collection('topics').doc(o.id)),
      relatedConcepts.map(o => db.collection('topics').doc(o.topicId))
    ),
    o => o.id
  );

  detailsData.relatedConcepts = relatedConcepts.map(o =>
    db.collection('publishedConcepts').doc(o.id)
  );

  const challengeRef = db.collection('challenges').doc(id);
  const detailsRef = getDetailsRef(challengeRef);

  await db.runTransaction(async transaction => {
    await transaction.update(challengeRef, docData);
    await transaction.update(detailsRef, detailsData);
  });
}

export default {
  add: addChallengeToDB,
  get: getChallengeFromDB,
  publish: publishChallenge,
  update: updateChallengeInDB
};
